import { useMemo } from 'react';

import { GridSortModel } from '@mui/x-data-grid-premium';

import { MAX_SORT_COLUMNS } from '@/shared/lib/sorting';

import {
  CycleSortLevelOptions,
  SortDirection,
  cycleSortLevel,
  moveSortLevel,
  removeSortLevel,
  setSortLevelDirection,
} from '../sortModel';

export interface UseSortModelActionsParams {
  sortModel: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
}

export interface UseSortModelActionsReturn {
  sortModel: GridSortModel;
  cycle: (field: string, options?: CycleSortLevelOptions) => void;
  setDirection: (field: string, direction: SortDirection) => void;
  remove: (field: string) => void;
  move: (field: string, delta: number) => void;
  clear: () => void;
  canAddLevel: boolean;
}

/**
 * Hook-free so tests and non-React callers can build the same action set.
 * The sortModel helpers return the original model for no-ops, which is what
 * lets `apply` skip pointless updates.
 */
export const createSortModelActions = (
  sortModel: GridSortModel,
  onSortModelChange?: (model: GridSortModel) => void,
): UseSortModelActionsReturn => {
  const apply = (next: GridSortModel) => {
    if (next !== sortModel) onSortModelChange?.(next);
  };

  return {
    sortModel,
    cycle: (field, options) => apply(cycleSortLevel(sortModel, field, options)),
    setDirection: (field, direction) => apply(setSortLevelDirection(sortModel, field, direction)),
    remove: (field) => apply(removeSortLevel(sortModel, field)),
    move: (field, delta) => apply(moveSortLevel(sortModel, field, delta)),
    clear: () => apply(sortModel.length ? [] : sortModel),
    canAddLevel: sortModel.length < MAX_SORT_COLUMNS,
  };
};

const useSortModelActions = ({
  sortModel,
  onSortModelChange,
}: UseSortModelActionsParams): UseSortModelActionsReturn =>
  useMemo(
    () => createSortModelActions(sortModel, onSortModelChange),
    [sortModel, onSortModelChange],
  );

export default useSortModelActions;
